/**
 * SuggestDropdown — autocomplete suggestions dropdown
 */

import { createElement, clearChildren } from '../utils/dom.js';
import { EVENTS } from '../core/events.js';

const ICONS = {
  history: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
  category: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>',
  brand: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>',
  article: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7V4h16v3"/><path d="M9 20h6"/><path d="M12 4v16"/></svg>',
  product: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a4 4 0 00-8 0v2"/></svg>',
};

export default class SuggestDropdown {
  /**
   * @param {Object} eventBus - EventBus instance
   * @param {Object} i18n - I18n instance
   */
  constructor(eventBus, i18n) {
    this._eventBus = eventBus;
    this._i18n = i18n;

    this._el = null;
    this._activeIndex = -1;
    this._items = [];

    this._onDocClick = this._handleDocClick.bind(this);
    this._onDocKeydown = this._handleDocKeydown.bind(this);
  }

  /**
   * Render the dropdown container
   * @param {HTMLElement} container
   */
  render(container) {
    this._el = createElement('div', 'grb-widget--suggest');
    container.appendChild(this._el);
  }

  /**
   * Update suggestions list
   * @param {Array} suggestions - Array of { text, type, category_id }
   */
  update(suggestions) {
    if (!this._el) return;

    this._items = suggestions || [];
    this._activeIndex = -1;
    clearChildren(this._el);

    if (this._items.length === 0) {
      this.hide();
      return;
    }

    // Group by type
    let currentType = null;
    for (let i = 0; i < this._items.length; i++) {
      const item = this._items[i];

      if (item.type !== currentType) {
        currentType = item.type;
        const label = this._i18n.t('suggest.' + item.type) || item.type;
        const header = createElement('div', 'grb-widget--suggest-group', {}, [label]);
        this._el.appendChild(header);
      }

      const row = createElement('div', 'grb-widget--suggest-item', {
        'data-index': String(i),
      });

      const icon = createElement('span', 'grb-widget--suggest-icon');
      icon.innerHTML = ICONS[item.type] || ICONS.product;

      const text = createElement('span', 'grb-widget--suggest-text', {}, [item.text]);

      row.appendChild(icon);
      row.appendChild(text);

      row.addEventListener('click', () => {
        this._eventBus.emit(EVENTS.SUGGEST_SELECT, { text: item.text, type: item.type, category_id: item.category_id, vendor_id: item.vendor_id });
      });

      this._el.appendChild(row);
    }

    this.show();
  }

  /**
   * Navigate items with keyboard arrows
   * @param {'up'|'down'} direction
   */
  navigate(direction) {
    if (!this._items.length) return;

    const rows = this._el.querySelectorAll('.grb-widget--suggest-item');
    if (!rows.length) return;

    // Remove current highlight
    if (this._activeIndex >= 0 && rows[this._activeIndex]) {
      rows[this._activeIndex].classList.remove('grb-widget--suggest-item-active');
    }

    if (direction === 'down') {
      this._activeIndex = this._activeIndex < rows.length - 1 ? this._activeIndex + 1 : 0;
    } else {
      this._activeIndex = this._activeIndex > 0 ? this._activeIndex - 1 : rows.length - 1;
    }

    rows[this._activeIndex].classList.add('grb-widget--suggest-item-active');
    rows[this._activeIndex].scrollIntoView({ block: 'nearest' });
  }

  /**
   * Get the currently highlighted item text
   * @returns {string|null}
   */
  getActiveText() {
    if (this._activeIndex >= 0 && this._items[this._activeIndex]) {
      return this._items[this._activeIndex].text;
    }
    return null;
  }

  show() {
    if (this._el) {
      this._el.classList.add('grb-widget--suggest-visible');
      document.addEventListener('click', this._onDocClick, true);
      document.addEventListener('keydown', this._onDocKeydown, true);
    }
  }

  hide() {
    if (this._el) {
      this._el.classList.remove('grb-widget--suggest-visible');
      this._activeIndex = -1;
      document.removeEventListener('click', this._onDocClick, true);
      document.removeEventListener('keydown', this._onDocKeydown, true);
    }
  }

  isVisible() {
    return this._el ? this._el.classList.contains('grb-widget--suggest-visible') : false;
  }

  /** @private */
  _handleDocClick(e) {
    if (this._el && !this._el.contains(e.target)) {
      this.hide();
    }
  }

  /** @private */
  _handleDocKeydown(e) {
    if (e.key === 'Escape' && this.isVisible()) {
      e.stopPropagation();
      this.hide();
    }
  }

  destroy() {
    document.removeEventListener('click', this._onDocClick, true);
    document.removeEventListener('keydown', this._onDocKeydown, true);
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
    }
    this._el = null;
    this._items = [];
  }
}
